<template>
  <section class="archive-info-panel">
    <template v-if="!showEditor">
      <close-button
        class="close-button"
        @click="$modal.hideAll()"
      ></close-button>
      <header class="header">
        <span class="fs-title"
          >Gebruikers van
          <span class="sub-color">{{ archive.name }}</span></span
        >
      </header>
      <main class="main">
        <table>
          <colgroup>
            <col />
            <col />
            <col style="width: 16px;" />
          </colgroup>
          <tbody>
            <tr v-for="(user, index) in users" :key="index">
              <td class="user-cell">
                <span style="display: inline-block; width: 200px;">{{
                  user.email
                }}</span>
                <span style="display: inline-block; width: 10px;"></span>
                <span style="display: inline-block;">{{ user.name }}</span>
              </td>
              <td>{{ user.accessLevel }}</td>
              <td>
                <popup-menu anchor-position="right">
                  <button class="kebab-btn" slot="trigger">
                    <span></span>
                  </button>
                  <menu-item
                    v-if="user.pending"
                    @click="() => withdrawInvitation(user)"
                    >Uitnodiging intrekken
                  </menu-item>
                  <menu-item v-else @click="() => terminateAccess(user)"
                    >Toegang beëindigen
                  </menu-item>
                </popup-menu>
              </td>
            </tr>
          </tbody>
        </table>
      </main>
      <footer class="footer">
        <button class="pill-button" @click="toggleEditor">
          gebruiker toevoegen
        </button>
      </footer>
    </template>

    <template v-else>
      <header class="header">
        <span class="fs-title">Gebruiker toevoegen</span>
        <close-button @click="$modal.hideAll()"></close-button>
      </header>
      <main class="main">
        <form name="inviteForm" novalidate class="add-user-form">
          <label>Aan</label>
          <input
            type="text"
            placeholder="E-mail adres"
            class="mail-input"
            name="email"
            required
            v-model="user.email"
            :class="{ invalid: $v.user.email.$invalid }"
            ref="emails"
          />

          <label>Rechten</label>
          <section class="invite-container">
            <select-box
              @action="setAccessLevel('read')"
              :selected="user.accessLevel === 'read'"
            ></select-box>
            <div @click="setAccessLevel('read')" class="pointer">
              <h3>Beperkt</h3>
              <p>Bestanden bekijken en downloaden.</p>
            </div>
            <select-box
              @action="setAccessLevel('write')"
              :selected="user.accessLevel === 'write'"
            ></select-box>
            <div @click="setAccessLevel('write')" class="pointer">
              <h3>Uitgebreid</h3>
              <p>Bestanden bekijken, downloaden, toevoegen en verwijderen.</p>
            </div>
            <select-box
              @action="setAccessLevel('admin')"
              :selected="user.accessLevel === 'admin'"
            ></select-box>
            <div @click="setAccessLevel('admin')" class="pointer">
              <h3>Beheer</h3>
              <p>
                Bestanden bekijken, downloaden, toevoegen en verwijderen.
                Gebruikers toevoegen en verwijderen.
              </p>
            </div>
          </section>

          <label>Bericht</label>
          <textarea
            class="message"
            placeholder="Typ hier uw bericht voor de ontvanger(s)"
            v-model="user.message"
          ></textarea>
        </form>
        <p class="info-link">
          Aan het toevoegen van gebruikers zijn kosten verbonden.
          <a
            href="/docs/Voorwaarden voor het gebruik van FileStore.pdf"
            target="_blank"
            class="terms-link"
            >Klik hier voor de gebruiksvoorwaarden.
          </a>
        </p>
      </main>
      <footer class="footer">
        <button class="pill-button secondary" @click="toggleEditor">
          terug
        </button>
      </footer>

      <transition type="fade">
        <div class="overlay-message" v-if="invitedSuccessfully">
          <p>
            Uw uitnodiging is succesvol verzonden.
          </p>
        </div>
      </transition>
    </template>
  </section>
</template>

<script>
import FileNavigationMixin from '@/features/filestore/FileNavigationMixin.js'

import { email, required } from '@vuelidate/validators'
import {
  userCancel,
  userList,
  userRemove
} from '@/features/filestore/FileStoreApi'
import { mapStores } from 'pinia'
import { useFileStoreStore } from '@/features/filestore/FileStore.store'

export default {
  name: 'ArchiveUsers',
  props: ['archiveId'],
  mixins: [FileNavigationMixin],
  data() {
    return {
      showEditor: false,
      user: {
        accessLevel: 'read',
        message: undefined,
        email: undefined
      },
      invitedSuccessfully: false,
      users: []
    }
  },
  computed: {
    ...mapStores(useFileStoreStore),
    archive() {
      return this.fileStoreStore.getArchive(this.archiveId) || {}
    }
  },
  created() {
    userList(this.archive.folderId).then((users) => (this.users = users))
  },
  methods: {
    withdrawInvitation(user) {
      this.confirmationDialog(
        `Weet u zeker dat u de toegang tot <strong>${this.archive.name}</strong> wilt intrekken?`,
        'intrekken',
        () =>
          userCancel(this.archive.folderId, user.email).then(
            (users) => (this.users = users)
          )
      )
    },
    terminateAccess(user) {
      this.confirmationDialog(
        `Weet u zeker dat u de uitnodiging verzonden aan <strong>${user.email}</strong> wilt intrekken?`,
        'intrekken',
        () =>
          userRemove(this.archive.folderId, user.userId).then(
            (users) => (this.users = users)
          )
      )
    },
    setAccessLevel(level) {
      this.user.accessLevel = level
    },
    toggleEditor() {
      this.showEditor = !this.showEditor
      if (this.showEditor) {
        this.$nextTick(() => {
          this.$refs.emails.focus()
        })
      }
    }
  },
  validations: {
    user: {
      email: { required, email }
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../../../sass/helpers/_variables' as *;

.archive-info-panel {
  padding: 25px;
  height: 100%;
  //width: 100%;
  display: grid;
  grid-template-rows: 35px auto 30px;

  @media (max-width: $mobile-max-width) {
    grid-template-columns: calc(100vw - 50px);
  }

  .header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;

    h3 {
      font-size: 15px;
      font-weight: 500;
      color: var(--sub-color);
    }
  }

  table {
    width: 100%;

    td {
      vertical-align: top;
      line-height: 24px;
      padding: 6px 0;

      .user-cell {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        width: 100%;
        span {
          display: inline-block;
          //width: 100%;
        }
      }
    }
  }

  .footer {
    display: flex;
    justify-content: flex-end;

    .pill-button {
      margin-left: 8px;
    }
  }
}

.add-user-form {
  margin-top: 16px;
  display: grid;
  grid-template-columns: 85px auto;
  grid-row-gap: 16px;

  @media (max-width: $mobile-max-width) {
    grid-template-columns: 85px calc(100vw - 85px - 50px);
  }
}

.invite-container {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 24px 140px);

  @media (max-width: $mobile-max-width) {
    grid-template-columns: 24px 140px;
  }

  h3 {
    font-weight: 600;
    margin-bottom: 4px;
  }

  .select-box {
    margin-top: 2px;
  }

  .pointer p {
    margin-bottom: 8px;
  }
}

.message {
  height: 120px;
  //@media (max-width: $mobile-max-width) {
  //  height: 120px;
  //}
}

.overlay-message {
  background-color: rgba(255, 255, 255, 0.95);
}
</style>
